import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule } from '@angular/common/http';
import { NvD3Module } from '../../../widgets/nvd3';

import { UIRouter, TransitionService, UIRouterModule } from '@uirouter/angular';

import { TrackerLibModule } from '../../../plugins/tracker/lib/tracker.lib.module';
import { CoreFiltersModule } from '../../core.filters.module';
import { CoreWidgetsModule } from '../../../widgets/core.widgets.module';

import { OrgSelectorComponent } from './lib/orgSelector/org.selector.component';
import { ScmAclService } from './services/project.scm.acl.service';
import { ProjectSprintData } from './services/project.sprint.data.service';
import { ProjectReportsData } from './services/project.reports.data.service';

import { ProjectResolvesService } from './project-resolves.service';
import { ProjectAdminResolvesService } from './project-admin-resolves.service';

import { ProjectActivityChartComponent } from './projectActivityChart/project.activity.chart.component';
import { ProjectListComponent } from './projectList/project-list.component';
import { OrgMetricsComponent } from './orgMetrics/org.metrics.component';
import { ProjectBuildsComponent } from './projectBuilds/project.builds.component';
import { ProjectJoinButtonComponent } from './projectJoinButton/project.join-button.component';
import { ProjectHeaderComponent } from './projectHeader/project.header.component';
import { ProjectMainViewComponent } from './projectMain/project.main.view.component';
import { ProjectStandupComponent } from './projectStandup/project.standup.component';

import { ProjectRegistrationHelper } from './projectRegistration/project.registration.helper';
import { ProjectRegistrationComponent } from './projectRegistration/project.registration.component';
import { ProjectRegistrationMainComponent } from './projectRegistration/projectRegistrationMain/project.registration.main.component';
import { ProjectRegistrationModulesComponent } from './projectRegistration/projectRegistrationModules/project.registration.modules.component';
import { ProjectRegistrationFinishComponent } from './projectRegistration/projectRegistrationFinish/project.registration.finish.component';

import { OrgRegistrationComponent } from './projectRegistration/orgRegistration/org.registration.component';
import { OrgRegistrationMainComponent } from './projectRegistration/orgRegistrationMain/org.registration.main.component';

import { ProjectSprintAddModalComponent } from './projectSprints/projectSprintAdd/project.sprint.add.component';
import { ProjectSprintEditComponent } from './projectSprints/projectSprintEdit/project.sprint.edit.component';
import { ProjectSprintDeleteComponent } from './projectSprints/projectSprintDelete/project.sprint.delete.component';
import { ProjectSprintCutoverComponent } from './projectSprints/projectSprintCutover/project.sprint.cutover.component';

import { ProjectSprintDetailsClosedComponent } from './projectSprints/projectSprintDetail/projectSprintDetailsClosed/project.sprint.details.closed.component';
import { ProjectSprintDetailsOpenComponent } from './projectSprints/projectSprintDetail/projectSprintDetailsOpen/project.sprint.details.open.component';
import { ProjectSprintDetailComponent } from './projectSprints/projectSprintDetail/project.sprint.detail.component';
import { ProjectSprintBurndownComponent } from './projectSprints/projectSprintBurndown/project.sprint.burndown.component';
import { ProjectSprintsComponent } from './projectSprints/project.sprints.component';

import { ProjectReportSelectorComponent } from './reports/project-report-selector/project.report-selector.component';
import { ProjectReportHeaderComponent } from './reports/project-report-header/project.report-header.component';
import { ProjectReportButtonHeader } from './reports/project-report-button-header/project.report-button-header.component';

import { ProjectSummaryReportComponent } from './reports/project-summary-report/project.report.summary.component';

import { TeamReportHelper } from './reports/project-team-report/project.report.team.service.component';
import { ProjectTeamReportComponent } from './reports/project-team-report/project.report.team.component';

import { ScmReportHelper } from './reports/project-scm-report/project.report.scm.service.component';
import { ProjectScmReportComponent } from './reports/project-scm-report/project.report.scm.component';

import { FrsReportHelper } from './reports/project-frs-report/project.report.frs.service.component';
import { ProjectFrsReportComponent } from './reports/project-frs-report/project.report.frs.component';

import { ProjectDiskReportComponent } from './reports/project-disk-report/project.report.disk.component';

import { ActivityReportHelper } from './reports/project-activity-report/project.report.activity.service.component';
import { ProjectActivityReportComponent } from './reports/project-activity-report/project.report.activity.component';

import { TrackerReportHelper } from './reports/project-tracker-report/project.report.tracker.service.component';
import { ProjectTrackerReportComponent } from './reports/project-tracker-report/project.report.tracker.component';

import { ProjectTroveComponent } from './projectTrove/project.trove.component';
import { ProjectTrackersComponent } from './projectTrackers/project.trackers.component';

import { ProjectTeamInvitesComponent } from './projectTeam/projectTeamInvites/project.team.invites.component';
import { ProjectTeamJoinsComponent } from './projectTeam/projectTeamJoins/project.team.joins.component';
import { ProjectTeamMembersComponent } from './projectTeam/projectTeamMembers/project.team.members.component';
import { ProjectTeamAddMemberComponent } from './projectTeam/projectTeamAddMember/project.team.addmember.component';
import { ProjectTeamComponent } from './projectTeam/project.team.component';

import { ProjectHomeComponent } from './projectHome/project.home.component';
import { ProjectSummaryOrgComponent } from './projectHome/project.summary.org.component';
import { ProjectSummaryProjectComponent } from './projectHome/project.summary.project.component';

import { OrgMembersAuditComponent } from '../admin/orgs/orgMembersAudit/org.members-audit.component';

import { ProjectAdminModule } from './admin/project.admin.module';

import { PROJECT_STATES } from './project.routes.config';
import { PROJECT_REPORTS_STATES } from './reports/project.reports.routes.config';

@NgModule({
    imports: [
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        CoreFiltersModule,
        CoreWidgetsModule,
        TrackerLibModule,
        NvD3Module,
        ProjectAdminModule,
        UIRouterModule.forChild({
            states: [...PROJECT_STATES, ...PROJECT_REPORTS_STATES]
        })
    ],
    providers: [
        ScmAclService,
        ProjectSprintData,
        ProjectReportsData,
        TeamReportHelper,
        ScmReportHelper,
        FrsReportHelper,
        ActivityReportHelper,
        TrackerReportHelper,
        ProjectRegistrationHelper,
        ProjectResolvesService,
        ProjectAdminResolvesService,
    ],
    declarations: [
        OrgSelectorComponent,
        ProjectActivityChartComponent,
        OrgMetricsComponent,
        ProjectBuildsComponent,
        ProjectJoinButtonComponent,
        ProjectHeaderComponent,
        ProjectMainViewComponent,
        ProjectStandupComponent,
        ProjectReportSelectorComponent,
        ProjectReportHeaderComponent,
        ProjectReportButtonHeader,
        ProjectSummaryReportComponent,
        ProjectTeamReportComponent,
        ProjectScmReportComponent,
        ProjectFrsReportComponent,
        ProjectDiskReportComponent,
        ProjectActivityReportComponent,
        ProjectTrackerReportComponent,
        ProjectTroveComponent,
        ProjectTrackersComponent,
        ProjectTeamInvitesComponent,
        ProjectTeamJoinsComponent,
        ProjectTeamMembersComponent,
        ProjectTeamAddMemberComponent,
        ProjectTeamComponent,
        ProjectSprintAddModalComponent,
        ProjectSprintEditComponent,
        ProjectSprintDeleteComponent,
        ProjectSprintCutoverComponent,
        ProjectSprintDetailsClosedComponent,
        ProjectSprintDetailsOpenComponent,
        ProjectSprintDetailComponent,
        ProjectSprintBurndownComponent,
        ProjectSprintsComponent,
        ProjectRegistrationComponent,
        ProjectRegistrationMainComponent,
        ProjectRegistrationModulesComponent,
        ProjectRegistrationFinishComponent,
        OrgRegistrationComponent,
        OrgRegistrationMainComponent,
        ProjectListComponent,
        ProjectHomeComponent,
        ProjectSummaryOrgComponent,
        ProjectSummaryProjectComponent,
        OrgMembersAuditComponent
    ],
    exports: [
        ProjectHeaderComponent,
        ProjectHomeComponent,
        OrgMembersAuditComponent,
        // #65237: Breakdown tab reuses <project-sprint-burndown> via its [burndown] input.
        ProjectSprintBurndownComponent
    ]
})
export class ProjectModule {
    constructor() { }
}
